import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { MagazineCard } from "../components/site/MagazineCard";

const FREQUENCIES = ["Weekly", "Fortnightly", "Monthly", "Quarterly"];

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const [mags, setMags] = useState([]);
  const [cats, setCats] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(12);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.categories));
    api.get("/publishers").then((r) => setPubs(r.publishers));
  }, []);

  useEffect(() => {
    setLoading(true);
    setVisible(12);
    api.get("/magazines", Object.fromEntries(params.entries()))
      .then((r) => setMags(r.magazines))
      .catch(() => setMags([]))
      .finally(() => setLoading(false));
  }, [params]);

  const clearAll = () => { setSearch(""); setParams({}); };

  const set = (k, v) => {
    const p = Object.fromEntries(params.entries());
    if (v) p[k] = v; else delete p[k];
    setParams(p);
  };

  const activeCat = params.get("category") || "";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 md:py-16" data-testid="catalog-page">
      <SEO title="Browse Magazines" path="/magazines" description="Browse and subscribe to India's best magazines by category, publisher, language and frequency." />
      <div className="max-w-2xl">
        <span className="eyebrow">The Catalog</span>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-3 text-bm-ink">Browse every title</h1>
        <p className="text-bm-body mt-4 leading-relaxed">Every periodical worth your reading time - filter by category, publisher or frequency.</p>
      </div>

      <form className="relative mt-10 max-w-md" onSubmit={(e) => { e.preventDefault(); set("search", search); }} data-testid="catalog-search-form">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-bm-muted" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search magazines..."
          data-testid="catalog-search-input" className="bm-input !pl-11 !rounded-full" />
      </form>

      <div className="flex flex-wrap gap-3 mt-8" data-testid="category-filters">
        <button onClick={() => set("category", "")} data-testid="filter-category-all"
          className={`chip ${!activeCat ? "chip-active" : "hover:bg-bm-sand-dark"}`}>
          All
        </button>
        {cats.map((c) => (
          <button key={c.id} onClick={() => set("category", c.slug === activeCat ? "" : c.slug)} data-testid={`filter-category-${c.slug}`}
            className={`chip ${activeCat === c.slug ? "chip-active" : "hover:bg-bm-sand-dark"}`}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-4 mt-6 items-center" data-testid="secondary-filters">
        <select value={params.get("publisher") || ""} onChange={(e) => set("publisher", e.target.value)} data-testid="filter-publisher"
          className="bm-input !w-auto !rounded-full">
          <option value="">All Publishers</option>
          {pubs.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
        </select>
        <select value={params.get("frequency") || ""} onChange={(e) => set("frequency", e.target.value)} data-testid="filter-frequency"
          className="bm-input !w-auto !rounded-full">
          <option value="">Any Frequency</option>
          {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={params.get("sort") || "newest"} onChange={(e) => set("sort", e.target.value)} data-testid="filter-sort"
          className="bm-input !w-auto !rounded-full">
          <option value="newest">Newest</option>
          <option value="popular">Most Popular</option>
          <option value="title">A - Z</option>
        </select>
        <span className="text-sm text-bm-muted" data-testid="results-count">{mags.length} titles</span>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 mt-14" data-testid="catalog-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bm-card p-4 animate-pulse">
              <div className="w-full aspect-[3/4] rounded-lg bg-bm-sand/60" />
              <div className="h-3 bg-bm-sand/60 rounded mt-4 w-1/2" />
              <div className="h-5 bg-bm-sand/60 rounded mt-2 w-3/4" />
              <div className="h-4 bg-bm-sand/60 rounded mt-3 w-2/3" />
            </div>
          ))}
        </div>
      ) : mags.length ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 mt-14">
            {mags.slice(0, visible).map((m) => <MagazineCard key={m.id} mag={m} />)}
          </div>
          {visible < mags.length && (
            <div className="text-center mt-12">
              <button onClick={() => setVisible((v) => v + 12)} data-testid="load-more-btn" className="btn-secondary">
                Load more ({mags.length - visible} remaining)
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-24" data-testid="no-results">
          <p className="font-display text-2xl font-semibold text-bm-ink">No magazines found</p>
          <p className="text-bm-muted mt-2">Nothing matches these filters. Try a different category or search term.</p>
          <button onClick={clearAll} data-testid="clear-filters-btn" className="btn-primary mt-6">Clear all filters</button>
        </div>
      )}
    </div>
  );
}
